import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  ReplaySubject,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { PaymentMethodService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-payment-method-picker',
  templateUrl: './payment-method-picker.component.html',
  standalone: false,
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: PaymentMethodPickerComponent }, //
  ],
})
export class PaymentMethodPickerComponent implements ControlValueAccessor, OnDestroy {
  @HostBinding('class') protected _classes = 'block';

  @PropertyListener('userId') private userId$ = new BehaviorSubject<string | null>(null);
  @Input() userId: string | null = null;

  @PropertyListener('gatewayInstanceId') private gatewayInstanceId$ = new BehaviorSubject<string | null>(null);
  @Input() gatewayInstanceId: string | null = null;

  @Input() set autoSelectFirst(autoSelectFirst: BooleanInput) {
    this._autoSelectFirst = coerceBooleanProperty(autoSelectFirst);
  }
  get autoSelectFirst() {
    return this._autoSelectFirst;
  }
  private _autoSelectFirst = true;

  @Input() emptySelectionLabel = 'No payment method selected';

  @Input() set value(value: string | null) {
    this.control.setValue(value);
  }
  get value() {
    return this.control.valid ? this.control.value : null;
  }

  @Input() set disabled(disabled: BooleanInput) {
    if (coerceBooleanProperty(disabled)) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }
  get disabled() {
    return this.control.disabled;
  }

  protected control = this.formBuilder.control(null as string | null, Validators.required);

  private refresh$ = new BehaviorSubject<null>(null);
  protected paymentMethods$ = combineLatest([
    this.userId$.pipe(distinctUntilChanged()),
    this.gatewayInstanceId$.pipe(distinctUntilChanged()),
    this.refresh$,
  ]).pipe(
    switchMap(([userId, gatewayInstanceId]) =>
      from(
        this.paymentMethodSvc.list({
          limit: 100,
          userId: userId ?? undefined,
          gatewayInstanceId: gatewayInstanceId ?? undefined,
          forCheckout: true,
        })
      ).pipe(
        // Runs per fetched list, inside the switchMap so a stale list can't re-select its own card.
        map(data => ({
          ...data,
          items:
            gatewayInstanceId === null
              ? data.items
              : data.items.filter(method => method.gatewayInstanceId === gatewayInstanceId),
        })),
        tap(data => {
          const hadSelection = !!this.control.value;

          // Drop a selection this list doesn't offer, but never swap in a different card —
          // retry payment and update billing preset a specific method on purpose.
          if (hadSelection && !data.items.some(pm => pm.id === this.control.value)) {
            this.control.setValue(null);
          } else if (this.autoSelectFirst && !hadSelection) {
            this.value = data.items[0]?.id ?? null;
          }
        })
      )
    ),
    switchMap(data => {
      return this.control.valueChanges.pipe(
        startWith(this.control.value),
        map(paymentMethodId => {
          return data.items.sort((a, b) =>
            a.id === paymentMethodId ? -1 : b.id === paymentMethodId ? 1 : a.created.getTime() - b.created.getTime()
          );
        })
      );
    }),
    shareReplay(1)
  );

  // combineLatest, not switchMap over valueChanges: auto-select writes the control mid-broadcast,
  // which used to leave this resolving against the old list and settling on null.
  protected selectedPaymentMethod$ = combineLatest([
    this.paymentMethods$,
    this.control.valueChanges.pipe(startWith(null)),
  ]).pipe(
    map(([paymentMethods]) => paymentMethods.find(pm => pm.id === this.value) ?? null),
    distinctUntilChanged(),
    shareReplay(1)
  );

  protected overlayOpen = false;

  private onDestroy$ = new Subject<void>();
  protected touched$ = new ReplaySubject<void>(1);

  constructor(
    private formBuilder: FormBuilder,
    private paymentMethodSvc: PaymentMethodService
  ) {}

  writeValue(value: string | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.control.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => fn(this.value));
  }

  registerOnTouched(fn: () => void): void {
    this.touched$.pipe(takeUntil(this.onDestroy$)).subscribe(() => fn());
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  refresh() {
    this.refresh$.next(null);
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();

    this.touched$.complete();
    this.refresh$.complete();
  }
}
